import React from 'react';
import { TrainingMetrics } from '../../types/athlete';
import { Calendar, Clock, CheckCircle, TrendingUp, Droplets, Bike, PersonStanding, Dumbbell, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface DashboardStatsProps {
  metrics: TrainingMetrics;
  loading?: boolean;
}

const disciplineIcons = {
  swim: <Droplets className="text-blue-400" size={16} />,
  bike: <Bike className="text-green-400" size={16} />,
  run: <PersonStanding className="text-orange-400" size={16} />,
  strength: <Dumbbell className="text-purple-400" size={16} />,
};

const DashboardStats: React.FC<DashboardStatsProps> = ({ metrics, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="card-base p-6 animate-pulse">
            <div className="h-4 bg-[#2d3748] rounded w-1/2 mb-4" />
            <div className="h-8 bg-[#2d3748] rounded w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  const formatMinutes = (mins: number): string => {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className="space-y-8">
      {/* This Week Stats */}
      <section>
        <h2 className="text-xl font-bold mb-4">This Week</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-base p-6">
            <div className="flex items-center justify-between mb-2">
              <Calendar size={20} className="text-[#3b82f6]" />
              <span className="text-xs text-muted">Sessions</span>
            </div>
            <p className="text-3xl font-bold">
              {metrics.thisWeek.completedSessions}
              <span className="text-lg text-muted">/{metrics.thisWeek.totalSessions}</span>
            </p>
            <p className="text-sm text-muted mt-1">
              {metrics.thisWeek.completionRate}% complete
            </p>
          </div>

          <div className="card-base p-6">
            <div className="flex items-center justify-between mb-2">
              <Clock size={20} className="text-green-400" />
              <span className="text-xs text-muted">Time</span>
            </div>
            <p className="text-3xl font-bold">{formatMinutes(metrics.thisWeek.totalActualMinutes)}</p>
            <p className="text-sm text-muted mt-1">
              of {formatMinutes(metrics.thisWeek.totalPlannedMinutes)} planned
            </p>
          </div>

          <div className="card-base p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle size={20} className="text-orange-400" />
              <span className="text-xs text-muted">Streak</span>
            </div>
            <p className="text-3xl font-bold">{metrics.thisWeek.streak}</p>
            <p className="text-sm text-muted mt-1">days in a row</p>
          </div>

          <div className="card-base p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp size={20} className="text-purple-400" />
              <span className="text-xs text-muted">Discipline</span>
            </div>
            <div className="flex space-x-2 mt-1">
              {Object.entries(metrics.thisWeek.sessionsByDiscipline).map(([disc, count]) => (
                count > 0 && (
                  <div key={disc} className="flex items-center space-x-1">
                    {disciplineIcons[disc as keyof typeof disciplineIcons]}
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                )
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Goals Progress */}
      {metrics.goalsProgress.length > 0 && (
        <section>
          <h2 className="text-xl font-bold mb-4">Goals Progress</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {metrics.goalsProgress.slice(0, 6).map(({ goal, progressPercent }) => (
              <div key={goal.id} className="card-base p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {disciplineIcons[goal.discipline]}
                    <span className="font-medium capitalize">{goal.discipline}</span>
                  </div>
                  <span className="text-sm text-muted">{goal.unit}</span>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span>{goal.progress_current.toLocaleString()}</span>
                    <span>/ {goal.target_value.toLocaleString()} {goal.unit}</span>
                  </div>
                  <div className="h-2 bg-[#141921] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#3b82f6] rounded-full transition-all"
                      style={{ width: `${Math.min(progressPercent, 100)}%` }}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted">
                  {progressPercent}% complete
                  {goal.deadline_date && (
                    <> • Due {new Date(goal.deadline_date).toLocaleDateString()}</>
                  )}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Upcoming Sessions */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Upcoming Sessions</h2>
          <Link to="/plans" className="text-sm text-[#3b82f6] hover:underline flex items-center">
            View all <ChevronRight size={16} />
          </Link>
        </div>
        {metrics.upcomingSessions.length === 0 ? (
          <div className="card-base p-8 text-center text-muted">
            <Calendar className="mx-auto mb-4" size={48} />
            <p>No upcoming sessions scheduled</p>
            <Link
              to="/plans/new"
              className="inline-flex items-center mt-4 px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors"
            >
              Create a Plan
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {metrics.upcomingSessions.map((session) => (
              <div key={session.id} className="card-base p-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <p className="text-xs text-muted">
                      {new Date(session.scheduled_date).toLocaleDateString('en-US', { weekday: 'short' })}
                    </p>
                    <p className="text-lg font-bold">
                      {new Date(session.scheduled_date).getDate()}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium">{session.session_name}</p>
                    <p className="text-sm text-muted">
                      {session.plan_name} • {session.planned_duration} min
                    </p>
                  </div>
                </div>
                <Link
                  to={`/plans/${session.id.split('_')[0]}`}
                  className="p-2 hover:bg-[#2d3748] rounded transition-colors"
                >
                  <ChevronRight size={20} />
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* This Month Summary */}
      <section>
        <h2 className="text-xl font-bold mb-4">This Month</h2>
        <div className="card-base p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-3xl font-bold">{metrics.thisMonth.totalSessions}</p>
              <p className="text-sm text-muted">Total Sessions</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{formatMinutes(metrics.thisMonth.totalMinutes)}</p>
              <p className="text-sm text-muted">Total Time</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{formatMinutes(metrics.thisMonth.averagePerSession)}</p>
              <p className="text-sm text-muted">Avg per Session</p>
            </div>
          </div>

          {/* Discipline Breakdown */}
          <div>
            <p className="text-sm font-medium mb-3">Discipline Breakdown</p>
            <div className="space-y-2">
              {metrics.thisMonth.disciplineBreakdown.map(({ discipline, count, minutes }) => (
                <div key={discipline} className="flex items-center space-x-3">
                  {disciplineIcons[discipline as keyof typeof disciplineIcons]}
                  <span className="w-20 text-sm capitalize">{discipline}</span>
                  <div className="flex-1 h-4 bg-[#141921] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        discipline === 'swim' ? 'bg-blue-500' :
                        discipline === 'bike' ? 'bg-green-500' :
                        discipline === 'run' ? 'bg-orange-500' :
                        'bg-purple-500'
                      }`}
                      style={{
                        width: `${metrics.thisMonth.totalSessions > 0 ? (count / metrics.thisMonth.totalSessions) * 100 : 0}%`
                      }}
                    />
                  </div>
                  <span className="text-sm text-muted w-24 text-right">
                    {count} sessions • {formatMinutes(minutes)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DashboardStats;
